import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Heart, Search, Grid, List, Clock, Tag, Download, Trash2, Upload, FileDown, ToggleLeft, ToggleRight, X, Image as ImageIcon, Play, Film, Grid3X3, Image } from 'lucide-react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { 
  getAllFavorites, 
  removeWorkFavorite, 
  removeEpisodeFavorite, 
  removeImageFavorite,
  batchDeleteFavorites,
  exportFavorites,
  importFavorites,
  getFavoritesStats,
  request,
  type FavoritesData,
  type WorkFavorite,
  type EpisodeFavorite,
  type ImageFavorite 
} from '../api/fancaps'
import { getStoredToken } from '../utils/storage'
import AuthenticatedImage from '../components/AuthenticatedImage'

// 🔧 确保图片URL使用ant域名而不是cdni域名，并修复路径结构
const convertToAntDomain = (imageUrl: string): string => {
  // 🎯 修复：如果是代理URL（/api/images/xxx.jpg），直接返回，不需要转换
  if (imageUrl.startsWith('/api/images/')) {
    return imageUrl
  }
  
  if (imageUrl.includes('cdni.fancaps.net')) {
    // 提取图片ID（文件名）
    const fileName = imageUrl.split('/').pop() || ''
    return `https://ant.fancaps.net/${fileName}`
  }
  // 如果已经是ant域名但路径不对，也要修复
  if (imageUrl.includes('ant.fancaps.net') && imageUrl.includes('/file/fancaps-animeimages/')) {
    const fileName = imageUrl.split('/').pop() || ''
    return `https://ant.fancaps.net/${fileName}`
  }
  return imageUrl
}

// 时间格式化函数
const formatDateTime = (dateString: string): string => {
  if (!dateString) return '未知'
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '未知'
    
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(/\//g, '-')
  } catch (error) {
    console.error('时间格式化失败:', error)
    return '未知'
  }
}

const FavoritesPage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  // 🎯 提取作品ID的工具函数 - 改进电影URL处理
  const extractWorkId = useCallback((url: string): string => {
    console.log('🔍 提取作品ID from URL:', url)
    
    // 处理标准的showimages.php格式（TV和动漫）
    if (url.includes('showimages.php?')) {
      const workId = url.split('showimages.php?')[1]
      console.log('✅ 从showimages.php提取ID:', workId)
      return workId
    }
    
    // 处理episodeimages.php格式（集数页面）
    // 🎯 注意：这里提取episode ID是为了work导航，不是为了图片页面跳转
    if (url.includes('episodeimages.php?')) {
      const workId = url.split('episodeimages.php?')[1]
      console.log('✅ 从episodeimages.php提取ID:', workId)
      return workId
    }
    
    // 🎯 处理电影URL格式 - 电影通常使用不同的URL结构
    if (url.includes('/movies/')) {
      // 电影URL格式: https://fancaps.net/movies/movieimages.php?12345-Movie_Title
      if (url.includes('movieimages.php?')) {
        const workId = url.split('movieimages.php?')[1]
        console.log('✅ 从movieimages.php提取电影ID:', workId)
        return workId
      }
      
      // 其他电影URL格式处理
      const movieMatch = url.match(/\/movies\/[^?]*\?(.+)/)
      if (movieMatch) {
        console.log('✅ 通过电影URL正则提取ID:', movieMatch[1])
        return movieMatch[1]
      }
    }
    
    // 🎯 处理搜索历史格式的URL
    if (url.startsWith('search-history:') || url.startsWith('/search-library/')) {
      console.log('⚠️ 搜索历史格式URL，无法直接提取ID:', url)
      return url // 保持原样，后续会有特殊处理
    }
    
    // 🎯 通用URL参数提取 - 处理各种查询参数格式
    const queryMatch = url.match(/[?&]([^&=]+(?:[-_][^&=]*)*)/);
    if (queryMatch) {
      console.log('✅ 通过通用正则提取ID:', queryMatch[1])
      return queryMatch[1]
    }
    
    // 🎯 处理直接的ID格式（可能是已经处理过的）
    if (/^[\w\-_]+$/.test(url)) {
      console.log('✅ 检测到直接ID格式:', url)
      return url
    }
    
    console.warn('⚠️ 无法提取作品ID，返回原URL:', url)
    return url
  }, [])

  // 🎯 检测内容类型的工具函数 - 完善电影类型检测逻辑
  const detectContentType = useCallback((work: WorkFavorite): 'movie' | 'tv' | 'anime' => {
    console.log('🔍 检测内容类型:', work.title, 'type:', work.type, 'url:', work.original_url)
    
    // 🎯 策略1: 优先使用work.type字段
    if (work.type) {
      const lowerType = work.type.toLowerCase()
      if (lowerType.includes('movie') || lowerType.includes('电影') || lowerType.includes('film')) {
        console.log('✅ 通过type字段检测为电影:', work.type)
        return 'movie'
      }
      if (lowerType.includes('tv') || lowerType.includes('电视') || lowerType.includes('series')) {
        console.log('✅ 通过type字段检测为TV:', work.type)
        return 'tv'
      }
      if (lowerType.includes('anime') || lowerType.includes('动漫') || lowerType.includes('动画')) {
        console.log('✅ 通过type字段检测为动漫:', work.type)
        return 'anime'
      }
    }
    
    // 🎯 策略2: 通过URL路径检测
    if (work.original_url.includes('/movies/') || work.original_url.includes('movieimages.php')) {
      console.log('✅ 通过URL路径检测为电影')
      return 'movie'
    }
    if (work.original_url.includes('/tv/') || work.original_url.includes('tvimages.php')) {
      console.log('✅ 通过URL路径检测为TV')
      return 'tv'
    }
    if (work.original_url.includes('/anime/') || work.original_url.includes('animeimages.php')) {
      console.log('✅ 通过URL路径检测为动漫')
      return 'anime'
    }
    
    // 🎯 策略3: 通过标题关键词检测（作为补充）
    const lowerTitle = work.title.toLowerCase()
    const movieKeywords = ['movie', '电影', 'film', '剧场版', '大电影']
    const tvKeywords = ['season', 'series', '第', '季', 'episode', 'ep']
    
    if (movieKeywords.some(keyword => lowerTitle.includes(keyword))) {
      console.log('✅ 通过标题关键词检测为电影')
      return 'movie'
    }
    
    if (tvKeywords.some(keyword => lowerTitle.includes(keyword))) {
      console.log('✅ 通过标题关键词检测为TV')
      return 'tv'
    }
    
    // 🎯 策略4: 通过URL结构特征检测
    // 电影通常没有复杂的集数结构，URL相对简单
    if (work.original_url.includes('showimages.php') && !work.original_url.includes('episode')) {
      // 如果是showimages但不包含episode，可能是电影
      console.log('⚠️ 通过URL结构推测可能为电影')
      return 'movie'
    }
    
    // 默认返回动漫类型
    console.log('⚠️ 无法确定类型，默认为动漫')
    return 'anime'
  }, [])

  // 🎯 统一的作品点击处理函数 - 区分电影和TV/动漫的跳转逻辑
  const handleWorkClick = useCallback((work: WorkFavorite) => {
    console.log('🔍 点击收藏作品:', work.title, 'URL:', work.original_url)
    
    const contentType = detectContentType(work)
    const workId = extractWorkId(work.original_url)
    
    if (contentType === 'movie') {
      // 🎯 电影直接跳转到图片页面
      console.log('🎬 电影内容，直接跳转到图片页面')
      navigate(`/images/${encodeURIComponent(workId)}?title=${encodeURIComponent(work.title)}&contentType=movie`)
    } else {
      // 🎯 TV和动漫跳转到剧集页面
      console.log('📺 TV/动漫内容，跳转到剧集页面')
      navigate(`/episodes/${encodeURIComponent(workId)}?title=${encodeURIComponent(work.title)}&contentType=${contentType}`)
    }
  }, [navigate, detectContentType, extractWorkId])

  // 🎯 统一的作品跳转函数 - 复用作品收藏页面的成功逻辑（用于集数页面的作品标题点击）
  const navigateToWork = useCallback((workTitle: string) => {
    console.log('🔍 导航到作品:', workTitle)
    
    request<{results: any[]}>('/search/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: workTitle.toLowerCase(),
        content_type: 'all'
      })
    })
      .then(data => {
        if (data.results && data.results.length > 0) {
          // 🎯 精确匹配作品标题
          const normalizeTitle = (title: string) => {
            return title.toLowerCase()
              .replace(/[^\w\s]/g, '') // 移除标点符号
              .replace(/\s+/g, ' ') // 标准化空格
              .trim()
          }
          
          const normalizedWorkTitle = normalizeTitle(workTitle)
          console.log('🔍 标准化标题:', normalizedWorkTitle)
          
          // 查找最匹配的搜索结果
          const exactMatch = data.results.find((result: any) => {
            const normalizedResultTitle = normalizeTitle(result.title)
            console.log('🔍 比较:', normalizedResultTitle, '===', normalizedWorkTitle)
            return normalizedResultTitle === normalizedWorkTitle
          })
          
          if (exactMatch) {
            // 从搜索结果的URL中提取正确的show_id
            const urlMatch = exactMatch.url.match(/showimages\.php\?(.+)$/)
            if (urlMatch) {
              const correctShowId = urlMatch[1]
              console.log('✅ 找到正确的show_id:', correctShowId)
              navigate(`/episodes/${encodeURIComponent(correctShowId)}?title=${encodeURIComponent(exactMatch.title)}`)
              return
            }
          }
          
          // 如果没有精确匹配，尝试模糊匹配
          const partialMatch = data.results.find((result: any) => {
            const normalizedResultTitle = normalizeTitle(result.title)
            console.log('🔍 模糊匹配:', normalizedResultTitle, 'vs', normalizedWorkTitle)
            return normalizedResultTitle.includes(normalizedWorkTitle) || 
                   normalizedWorkTitle.includes(normalizedResultTitle)
          })
          
          if (partialMatch) {
            const urlMatch = partialMatch.url.match(/showimages\.php\?(.+)$/)
            if (urlMatch) {
              const correctShowId = urlMatch[1]
              console.log('✅ 找到模糊匹配的show_id:', correctShowId, '作品:', partialMatch.title)
              navigate(`/episodes/${encodeURIComponent(correctShowId)}?title=${encodeURIComponent(partialMatch.title)}`)
              return
            }
          }
          
          console.warn('❌ 在搜索结果中未找到匹配的作品:', workTitle)
          console.log('📋 可用搜索结果:', data.results.map((r: any) => r.title))
        } else {
          console.warn('❌ 搜索无结果:', workTitle)
        }
      })
      .catch(error => {
        console.error('❌ 搜索API失败:', error)
      })
  }, [navigate])
  
  // 标签页状态
  const [activeTab, setActiveTab] = useState<'works' | 'episodes' | 'images'>('works')
  
  // 显示模式状态
  const [episodeDisplayMode, setEpisodeDisplayMode] = useState<'by_work' | 'by_episode'>('by_work')
  const [imageDisplayMode, setImageDisplayMode] = useState<'by_work' | 'by_episode' | 'direct'>('by_work')
  
  // 搜索状态
  const [searchQuery, setSearchQuery] = useState('')
  
  // 选择状态（合并删除和下载的选择，统一管理）
  const [selectedItems, setSelectedItems] = useState<{
    works: string[]
    episodes: string[]
    images: string[]
  }>({
    works: [],
    episodes: [],
    images: []
  })
  
  // 图片下载尺寸选择
  const [downloadImageSize, setDownloadImageSize] = useState<'small' | 'large'>('large')
  
  // 删除确认对话框状态
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean
    type: 'single' | 'batch'
    item?: any
    itemType?: string
  }>({
    show: false,
    type: 'single'
  })
  
  // 获取收藏数据
  const { data: favoritesData, isLoading, error } = useQuery({
    queryKey: ['favorites'],
    queryFn: getAllFavorites
  })
  
  // 获取收藏统计
  const { data: statsData } = useQuery({
    queryKey: ['favorites-stats'],
    queryFn: getFavoritesStats
  })
  
  // 删除收藏的mutation
  const deleteMutation = useMutation({
    mutationFn: async ({ type, id, imageId }: { type: string, id: string, imageId?: string }) => {
      switch (type) {
        case 'work': return await removeWorkFavorite(id)
        case 'episode': return await removeEpisodeFavorite(id)
        case 'image': 
          // 对于图片收藏，使用image_id而不是收藏记录的id
          return await removeImageFavorite(imageId || id)
        default: throw new Error('未知的删除类型')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
      queryClient.invalidateQueries({ queryKey: ['favorites-stats'] })
      setDeleteConfirm({ show: false, type: 'single' })
    }
  })
  
  // 批量删除的mutation
  const batchDeleteMutation = useMutation({
    mutationFn: async () => {
      return await batchDeleteFavorites(
        selectedItems.works,
        selectedItems.episodes,
        selectedItems.images
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
      queryClient.invalidateQueries({ queryKey: ['favorites-stats'] })
      setSelectedItems({ works: [], episodes: [], images: [] })
      setDeleteConfirm({ show: false, type: 'batch' })
    }
  })
  
  // 导出收藏数据
  const handleExport = async () => {
    try {
      const result = await exportFavorites()
      if (result.success) {
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { 
          type: 'application/json' 
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `favorites_backup_${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('导出失败:', error)
    }
  }
  
  // 处理删除确认
  const handleDeleteConfirm = () => {
    if (deleteConfirm.type === 'single' && deleteConfirm.item && deleteConfirm.itemType) {
      const deleteParams: { type: string, id: string, imageId?: string } = { 
        type: deleteConfirm.itemType, 
        id: deleteConfirm.item.id 
      }
      
      // 对于图片收藏，额外传递image_id
      if (deleteConfirm.itemType === 'image' && 'image_id' in deleteConfirm.item) {
        deleteParams.imageId = (deleteConfirm.item as any).image_id
      }
      
      deleteMutation.mutate(deleteParams)
    } else if (deleteConfirm.type === 'batch') {
      batchDeleteMutation.mutate()
    }
  }
  
  // 处理项目选择
  const handleItemSelect = (type: 'works' | 'episodes' | 'images', id: string, checked: boolean) => {
    setSelectedItems(prev => ({
      ...prev,
      [type]: checked 
        ? [...prev[type], id]
        : prev[type].filter(item => item !== id)
    }))
  }

  // 生成有效的文件名
  const generateValidFilename = (title: string): string => {
    if (!title || title.trim() === '') {
      return 'fancaps_favorites'
    }
    
    // 移除或替换特殊字符，保留中文、英文、数字
    let filename = title
      .replace(/[<>:"/\\|?*]/g, '') // 移除文件系统禁用字符
      .replace(/[\s\-_]+/g, '_') // 多个空格、横线、下划线替换为单个下划线
      .replace(/^_+|_+$/g, '') // 移除开头和结尾的下划线
    
    // 如果处理后为空或全是下划线，使用默认名称
    if (!filename || /^_*$/.test(filename)) {
      filename = 'fancaps_favorites'
    }
    
    // 长度限制，避免过长的文件名
    if (filename.length > 50) {
      filename = filename.substring(0, 50)
    }
    
    return filename
  }

  // 处理图片选择
  const handleImageSelect = (imageId: string, selected: boolean) => {
    console.log('handleImageSelect called:', { imageId, selected }) // 调试信息
    setSelectedItems(prev => ({
      ...prev,
      images: selected 
        ? [...prev.images, imageId]
        : prev.images.filter(id => id !== imageId)
    }))
  }

  // 图片下载功能
  const handleDownloadFavoriteImages = async () => {
    if (selectedItems.images.length === 0) {
      alert('请先选择要下载的图片')
      return
    }

    try {
      console.log('开始下载收藏图片...', selectedItems.images.length)
      
      // 构建参数化代理URL列表
      const sizeParam = downloadImageSize === 'small' ? 'small' : 'large'
      const imageUrls = selectedItems.images.map(imageId => {
        return `/api/images/${imageId}.jpg?size=${sizeParam}`
      })

      // 调用通用下载接口 - 修复API路径和参数
      const downloadData = await request<{download_id: string, file_count: number}>('/download/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_urls: imageUrls,
          filename_prefix: `收藏图片_${downloadImageSize === 'small' ? '小图' : '大图'}_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_')}`
        })
      })
      
      // 显示下载信息，包含尺寸说明
      const sizeDescription = downloadImageSize === 'small' 
        ? '📱 小图模式 - 快速下载，适合预览'
        : '🖼️ 大图模式 - 高清原图，适合收藏'
      
      alert(`下载任务已创建！\n${sizeDescription}\n任务ID: ${downloadData.download_id}\n将下载 ${downloadData.total_images} 张图片\n\n页面将在几秒后自动跳转到下载链接...`)
      
      // 轮询检查下载状态
      const checkDownloadStatus = async () => {
        try {
          const status = await request<{status: string, error?: string}>(`/download/${downloadData.download_id}/status`)
          
          if (status.status === 'completed') {
            // 下载完成，使用带认证的fetch请求下载文件
            try {
              const response = await fetch(`/api/download/${downloadData.download_id}/file`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${getStoredToken()}`,
                  'Content-Type': 'application/json'
                }
              })
              
              if (!response.ok) {
                throw new Error(`下载失败: ${response.status} ${response.statusText}`)
              }
              
              // 获取文件名
              const contentDisposition = response.headers.get('Content-Disposition')
              let filename = 'fancaps_favorites.zip'
              if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?=([^;]+)/)
                if (filenameMatch) {
                  filename = decodeURIComponent(filenameMatch[1].replace(/UTF-8''/, '').replace(/"/g, ''))
                }
              }
              
              // 创建blob并触发下载
              const blob = await response.blob()
              const url = window.URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.download = filename
              link.style.display = 'none'
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              window.URL.revokeObjectURL(url)
              
              console.log('✅ 文件下载成功:', filename)
            } catch (error) {
              console.error('下载文件失败:', error)
              alert(`下载文件失败: ${error.message}`)
            }
            return
          } else if (status.status === 'failed') {
            alert(`下载失败: ${status.error || '未知错误'}`)
            return
          }
          
          // 继续轮询
          setTimeout(checkDownloadStatus, 2000)
        } catch (error) {
          console.error('检查下载状态失败:', error)
        }
      }
      
      // 开始轮询
      setTimeout(checkDownloadStatus, 2000)
    } catch (error: any) {
      console.error('下载图片失败:', error)
      alert(`下载失败: ${error.message}`)
    }
  }
  
  // 获取过滤后的数据
  const filteredData = useMemo(() => {
    if (!favoritesData?.data) return { works: [], episodes: [], images: [] }
    
    const { works, episodes, images } = favoritesData.data
    
    if (!searchQuery) return { works, episodes, images }
    
    const query = searchQuery.toLowerCase()
    
    return {
      works: works.filter(work => 
        work.title.toLowerCase().includes(query)
      ),
      episodes: episodes.filter(episode => 
        episode.title.toLowerCase().includes(query) ||
        episode.work_title.toLowerCase().includes(query)
      ),
      images: images.filter(image => 
        image.work_title.toLowerCase().includes(query) ||
        image.episode_title.toLowerCase().includes(query)
      )
    }
  }, [favoritesData?.data, searchQuery])
  
  // 渲染作品收藏标签页
  const renderWorksTab = () => {
    const works = filteredData.works
    
    return (
      <div className="space-y-4">
        {works.length === 0 ? (
          <div className="text-center py-12">
            <Film className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchQuery ? '没有找到匹配的作品收藏' : '还没有收藏任何作品'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {works.map((work) => (
              <div
                key={work.id}
                onClick={() => handleWorkClick(work)}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer relative"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <input
                        type="checkbox"
                        checked={selectedItems.works.includes(work.id)}
                        onChange={(e) => handleItemSelect('works', work.id, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="inline-block px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded">
                        {work.type}
                      </span>
                    </div>
                    <h3 className="font-medium text-gray-900 truncate">
                      {work.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      收藏时间：{formatDateTime(work.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-1">
                    {/* 原始页面链接按钮 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        window.open(work.original_url, '_blank')
                      }}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="访问原始页面"
                    >
                      🔗
                    </button>
                    {/* 删除按钮 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteConfirm({
                          show: true,
                          type: 'single',
                          item: work,
                          itemType: 'work'
                        })
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="删除收藏"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* 点击提示 */}
                <div className="text-center text-xs text-gray-400 mt-2">
                  {detectContentType(work) === 'movie' ? '点击查看图片' : '点击查看剧集'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
  
  // 按作品分组的集数数据 (移到组件顶层以遵循hooks规则)
  const episodesByWork = useMemo(() => {
    const episodes = filteredData.episodes
    const grouped: { [workTitle: string]: EpisodeFavorite[] } = {}
    episodes.forEach(episode => {
      if (!grouped[episode.work_title]) {
        grouped[episode.work_title] = []
      }
      grouped[episode.work_title].push(episode)
    })
    return grouped
  }, [filteredData.episodes])

  // 渲染集数收藏标签页
  const renderEpisodesTab = () => {
    const episodes = filteredData.episodes
    
    return (
      <div className="space-y-4">
        {/* 显示模式切换 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">显示模式：</span>
            <button
              onClick={() => setEpisodeDisplayMode(episodeDisplayMode === 'by_work' ? 'by_episode' : 'by_work')}
              className="flex items-center space-x-2 text-sm text-gray-700 hover:text-blue-600 transition-colors"
            >
              {episodeDisplayMode === 'by_work' ? (
                <>
                  <ToggleLeft className="w-5 h-5" />
                  <span>按作品分组</span>
                </>
              ) : (
                <>
                  <ToggleRight className="w-5 h-5" />
                  <span>按集数展示</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {episodes.length === 0 ? (
          <div className="text-center py-12">
            <Play className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchQuery ? '没有找到匹配的集数收藏' : '还没有收藏任何集数'}
            </p>
          </div>
        ) : episodeDisplayMode === 'by_work' ? (
          // 按作品分组显示
          <div className="space-y-6">
            {Object.entries(episodesByWork).map(([workTitle, workEpisodes]) => (
              <div key={workTitle} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 
                    className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation() // 阻止事件冒泡
                      navigateToWork(workTitle)
                    }}
                    title="点击跳转到剧集列表"
                  >
                    {workTitle}
                  </h3>
                  <p className="text-sm text-gray-500">共 {workEpisodes.length} 集</p>
                </div>
                <div className="p-4 space-y-3">
                  {workEpisodes.map((episode) => (
                    <div
                      key={episode.id}
                      onClick={() => {
                        // 🎯 修复：直接传递完整URL，保留路径信息(/tv/ 或 /anime/)
                        let episodeUrl = episode.episode_url
                        
                        // 如果不是完整URL，需要构造完整URL（这种情况应该很少见）
                        if (!episodeUrl.startsWith('https://fancaps.net/')) {
                          // 兼容旧格式：如果只是episode ID，默认构造为动漫URL
                          if (!episodeUrl.startsWith('/images/')) {
                            episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeUrl}`
                          } else {
                            // 如果是 /images/ 格式，提取ID后构造
                            const episodeId = episodeUrl.replace('/images/', '')
                            episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeId}`
                          }
                        }
                        
                        console.log('🔗 传递完整URL到后端:', episodeUrl)
                        navigate(`/images/${encodeURIComponent(episodeUrl)}?title=${encodeURIComponent(episode.title)}&workTitle=${encodeURIComponent(workTitle)}&episodeTitle=${encodeURIComponent(episode.title)}`)
                      }}
                      className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded hover:border-blue-200 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedItems.episodes.includes(episode.id)}
                          onChange={(e) => handleItemSelect('episodes', episode.id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        
                        {/* 🎯 暂时隐藏：精选预览图片 - 用户要求暂时隐藏集数收藏页的图片展示功能 */}
                        {false && episode.preview_images && episode.preview_images.length > 0 ? (
                          <div className="flex space-x-1">
                            {episode.preview_images.slice(0, 4).map((imageUrl, imgIndex) => (
                              <div key={imgIndex} className="w-16 h-16 overflow-hidden rounded border hover:border-blue-300 transition-colors">
                                <img
                                  src={convertToAntDomain(imageUrl)}
                                  alt={`${episode.title} 预览 ${imgIndex + 1}`}
                                  className="w-full h-full object-cover transition-transform hover:scale-105"
                                  loading="lazy"
                                  onError={(e) => {
                                    const target = e.target as HTMLElement
                                    target.style.display = 'none'
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="w-6 h-6 bg-gray-100 rounded border flex items-center justify-center">
                            <Play className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">
                            {episode.title}
                          </h4>
                          <p className="text-sm text-gray-500">
                            收藏时间：{formatDateTime(episode.created_at)}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs text-blue-600">
                              点击查看图片
                            </p>
                            {false && episode.preview_images && episode.preview_images.length > 0 && (
                              <span className="text-xs text-green-600">
                                {episode.preview_images.length} 张预览
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteConfirm({
                              show: true,
                              type: 'single',
                              item: episode,
                              itemType: 'episode'
                            })
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="删除收藏"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // 直接列表显示
          <div className="space-y-3">
            {episodes.map((episode) => (
              <div
                key={episode.id}
                onClick={() => {
                  // 🎯 修复：直接传递完整URL，保留路径信息(/tv/ 或 /anime/)
                  let episodeUrl = episode.episode_url
                  
                  // 如果不是完整URL，需要构造完整URL（这种情况应该很少见）
                  if (!episodeUrl.startsWith('https://fancaps.net/')) {
                    // 兼容旧格式：如果只是episode ID，默认构造为动漫URL
                    if (!episodeUrl.startsWith('/images/')) {
                      episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeUrl}`
                    } else {
                      // 如果是 /images/ 格式，提取ID后构造
                      const episodeId = episodeUrl.replace('/images/', '')
                      episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeId}`
                    }
                  }
                  
                  console.log('🔗 传递完整URL到后端:', episodeUrl)
                  navigate(`/images/${encodeURIComponent(episodeUrl)}?title=${encodeURIComponent(episode.title)}&workTitle=${encodeURIComponent(episode.work_title)}&episodeTitle=${encodeURIComponent(episode.title)}`)
                }}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-200 transition-colors cursor-pointer"
              >
                <div className="flex items-center space-x-3 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedItems.episodes.includes(episode.id)}
                    onChange={(e) => handleItemSelect('episodes', episode.id, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  
                  {/* 🎯 暂时隐藏：精选预览图片 - 用户要求暂时隐藏集数收藏页的图片展示功能 */}
                  {false && episode.preview_images && episode.preview_images.length > 0 ? (
                    <div className="flex space-x-1">
                      {episode.preview_images.slice(0, 4).map((imageUrl, imgIndex) => (
                        <div key={imgIndex} className="w-16 h-16 overflow-hidden rounded border hover:border-blue-300 transition-colors">
                          <img
                            src={convertToAntDomain(imageUrl)}
                            alt={`${episode.title} 预览 ${imgIndex + 1}`}
                            className="w-full h-full object-cover transition-transform hover:scale-105"
                            loading="lazy"
                            onError={(e) => {
                              const target = e.target as HTMLElement
                              target.style.display = 'none'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="w-6 h-6 bg-gray-100 rounded border flex items-center justify-center">
                      <Play className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">
                      {episode.work_title} - {episode.title}
                    </h4>
                    <p className="text-sm text-gray-500">
                      收藏时间：{formatDateTime(episode.created_at)}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-blue-600">
                        点击查看图片
                      </p>
                      {false && episode.preview_images && episode.preview_images.length > 0 && (
                        <span className="text-xs text-green-600">
                          {episode.preview_images.length} 张预览
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteConfirm({
                        show: true,
                        type: 'single',
                        item: episode,
                        itemType: 'episode'
                      })
                    }}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="删除收藏"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
  
  // 渲染图片收藏标签页
  // 按作品分组的图片数据 (移到组件顶层以遵循hooks规则)
  const imagesByWork = useMemo(() => {
    const images = filteredData.images
    const grouped: { [workTitle: string]: { [episodeTitle: string]: ImageFavorite[] } } = {}
    images.forEach(image => {
      if (!grouped[image.work_title]) {
        grouped[image.work_title] = {}
      }
      if (!grouped[image.work_title][image.episode_title]) {
        grouped[image.work_title][image.episode_title] = []
      }
      grouped[image.work_title][image.episode_title].push(image)
    })
    return grouped
  }, [filteredData.images])
  
  // 按集数分组的图片数据 (移到组件顶层以遵循hooks规则)
  const imagesByEpisode = useMemo(() => {
    const images = filteredData.images
    const grouped: { [episodeKey: string]: ImageFavorite[] } = {}
    images.forEach(image => {
      const key = `${image.work_title} - ${image.episode_title}`
      if (!grouped[key]) {
        grouped[key] = []
      }
      grouped[key].push(image)
    })
    return grouped
  }, [filteredData.images])

  const renderImagesTab = () => {
    const images = filteredData.images
    
    return (
      <div className="space-y-4">
        {/* 图片标签页的控制按钮 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* 显示模式切换 */}
            <button
              onClick={() => setImageDisplayMode('by_work')}
              className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                imageDisplayMode === 'by_work'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Grid3X3 className="w-4 h-4 mr-1" />
              按作品
            </button>
            <button
              onClick={() => setImageDisplayMode('by_episode')}
              className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                imageDisplayMode === 'by_episode'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <List className="w-4 h-4 mr-1" />
              按集数
            </button>
            <button
              onClick={() => setImageDisplayMode('direct')}
              className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                imageDisplayMode === 'direct'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Grid3X3 className="w-4 h-4 mr-1" />
              直接网格
            </button>
          </div>
        </div>
        
        {images.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchQuery ? '没有找到匹配的图片收藏' : '还没有收藏任何图片'}
            </p>
          </div>
        ) : imageDisplayMode === 'by_work' ? (
          // 按作品分组显示
          <div className="space-y-6">
            {Object.entries(imagesByWork).map(([workTitle, episodes]) => (
              <div key={workTitle} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 
                    className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation() // 阻止事件冒泡
                      navigateToWork(workTitle)
                    }}
                    title="点击跳转到剧集列表"
                  >
                    {workTitle}
                  </h3>
                  <p className="text-sm text-gray-500">
                    共 {Object.values(episodes).reduce((total, imgs) => total + imgs.length, 0)} 张图片
                  </p>
                </div>
                <div className="p-4 space-y-4">
                  {Object.entries(episodes).map(([episodeTitle, episodeImages]) => (
                    <div key={episodeTitle} className="space-y-2">
                      <h4 
                        className="font-medium text-gray-800 cursor-pointer hover:text-blue-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation() // 阻止事件冒泡
                          // 点击集数标题跳转到截图列表页
                          console.log('🔍 点击集数标题跳转到截图列表:', episodeTitle)
                          
                          // 从收藏的图片数据中获取集数信息
                          const firstImage = episodeImages[0]
                          if (firstImage && firstImage.episode_id) {
                            // 🎯 修复：直接传递完整URL，保留路径信息(/tv/ 或 /anime/)
                            let episodeUrl = firstImage.episode_id
                            
                            // 如果不是完整URL，需要构造完整URL
                            if (!episodeUrl.startsWith('https://fancaps.net/')) {
                              // 兼容旧格式：如果只是episode ID，默认构造为动漫URL
                              if (!episodeUrl.startsWith('/images/')) {
                                episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeUrl}`
                              } else {
                                // 如果是 /images/ 格式，提取ID后构造
                                const episodeId = episodeUrl.replace('/images/', '')
                                episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeId}`
                              }
                            }
                            
                            console.log('🔗 传递完整URL到后端:', episodeUrl)
                            console.log('📍 跳转到集数:', episodeUrl, '标题:', episodeTitle)
                            navigate(`/images/${encodeURIComponent(episodeUrl)}?title=${encodeURIComponent(episodeTitle)}&workTitle=${encodeURIComponent(workTitle)}&episodeTitle=${encodeURIComponent(episodeTitle)}`)
                          } else {
                            console.error('❌ 无法获取集数ID')
                          }
                        }}
                        title="点击跳转到截图列表"
                      >
                        {episodeTitle}
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                        {episodeImages.map((image) => (
                          <div key={image.id} className="relative group">
                            {/* 统一选择框 - 左上角 */}
                            <div className="absolute top-1 left-1 z-10">
                              <input
                                type="checkbox"
                                checked={selectedItems.images.includes(image.image_id)}
                                onChange={(e) => handleItemSelect('images', image.image_id, e.target.checked)}
                                onClick={(e) => e.stopPropagation()}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                title="选择此图片"
                              />
                            </div>
                            <AuthenticatedImage
                              src={`/api/images/${image.image_id}.jpg`}
                              alt={`图片 ${image.image_id}`}
                              className="w-full aspect-video object-cover rounded border hover:border-blue-300 transition-colors cursor-pointer"
                              loading="lazy"
                            />
                            {/* 统一删除按钮 - 右上角 */}
                            <button
                              onClick={() => setDeleteConfirm({
                                show: true,
                                type: 'single',
                                item: image,
                                itemType: 'image'
                              })}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              title="删除此图片"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : imageDisplayMode === 'by_episode' ? (
          // 按集数分组显示
          <div className="space-y-6">
            {Object.entries(imagesByEpisode).map(([episodeKey, episodeImages]) => (
              <div key={episodeKey} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 
                    className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation() // 阻止事件冒泡
                      // 点击集数标题跳转到截图列表页
                      console.log('🔍 点击集数标题跳转到截图列表:', episodeKey)
                      
                      // 从收藏的图片数据中获取集数信息
                      const firstImage = episodeImages[0]
                      if (firstImage && firstImage.episode_id) {
                        // 🎯 修复：直接传递完整URL，保留路径信息(/tv/ 或 /anime/)
                        let episodeUrl = firstImage.episode_id
                        
                        // 如果不是完整URL，需要构造完整URL
                        if (!episodeUrl.startsWith('https://fancaps.net/')) {
                          // 兼容旧格式：如果只是episode ID，默认构造为动漫URL
                          if (!episodeUrl.startsWith('/images/')) {
                            episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeUrl}`
                          } else {
                            // 如果是 /images/ 格式，提取ID后构造
                            const episodeId = episodeUrl.replace('/images/', '')
                            episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeId}`
                          }
                        }
                        
                        console.log('🔗 传递完整URL到后端:', episodeUrl)
                        console.log('📍 跳转到集数:', episodeUrl, '标题:', episodeKey)
                        navigate(`/images/${encodeURIComponent(episodeUrl)}?title=${encodeURIComponent(firstImage.episode_title || episodeKey)}&workTitle=${encodeURIComponent(firstImage.work_title || '未知作品')}&episodeTitle=${encodeURIComponent(firstImage.episode_title || episodeKey)}`)
                      } else {
                        console.error('❌ 无法获取集数ID')
                      }
                    }}
                    title="点击跳转到截图列表"
                  >
                    {episodeKey}
                  </h3>
                  <p className="text-sm text-gray-500">共 {episodeImages.length} 张图片</p>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                    {episodeImages.map((image) => (
                      <div key={image.id} className="relative group">
                        {/* 统一选择框 - 左上角 */}
                        <div className="absolute top-1 left-1 z-10">
                          <input
                            type="checkbox"
                            checked={selectedItems.images.includes(image.image_id)}
                            onChange={(e) => handleItemSelect('images', image.image_id, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            title="选择此图片"
                          />
                        </div>
                        
                        <AuthenticatedImage
                          src={`/api/images/${image.image_id}.jpg`}
                          alt={`图片 ${image.image_id}`}
                          className="w-full aspect-video object-cover rounded border hover:border-blue-300 transition-colors cursor-pointer"
                          loading="lazy"
                        />
                        {/* 统一删除按钮 - 右上角 */}
                        <button
                          onClick={() => setDeleteConfirm({
                            show: true,
                            type: 'single',
                            item: image,
                            itemType: 'image'
                          })}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          title="删除此图片"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // 直接网格显示
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {images.map((image) => (
              <div key={image.id} className="relative group">
                {/* 统一选择框 - 左上角 */}
                <div className="absolute top-1 left-1 z-10">
                  <input
                    type="checkbox"
                    checked={selectedItems.images.includes(image.image_id)}
                    onChange={(e) => handleItemSelect('images', image.image_id, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    title="选择此图片"
                  />
                </div>
                
                <AuthenticatedImage
                  src={`/api/images/${image.image_id}.jpg`}
                  alt={`图片 ${image.image_id}`}
                  className="w-full aspect-video object-cover rounded border hover:border-blue-300 transition-colors cursor-pointer"
                  loading="lazy"
                />
                {/* 图片信息悬停显示 */}
                <div className="absolute bottom-1 left-1 right-1 bg-black bg-opacity-70 text-white text-xs p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="truncate">{image.work_title}</p>
                  <p className="truncate">{image.episode_title}</p>
                </div>
                {/* 统一删除按钮 - 右上角 */}
                <button
                  onClick={() => setDeleteConfirm({
                    show: true,
                    type: 'single',
                    item: image,
                    itemType: 'image'
                  })}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  title="删除此图片"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回首页
          </button>
          <h1 className="text-2xl font-bold text-gray-900">我的收藏</h1>
        </div>
        
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">
              加载收藏数据失败: {error instanceof Error ? error.message : '未知错误'}
            </p>
          </div>
        </div>
      </div>
    )
  }
  
  const totalSelectedItems = selectedItems.works.length + selectedItems.episodes.length + selectedItems.images.length
  
  return (
    <div className="space-y-6">
      {/* 删除确认对话框 */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {deleteConfirm.type === 'single' ? '确认删除' : '批量删除确认'}
              </h3>
              <button
                onClick={() => setDeleteConfirm({ show: false, type: 'single' })}
                className="text-gray-400 hover:text-gray-600"
                disabled={deleteMutation.isPending || batchDeleteMutation.isPending}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              {deleteConfirm.type === 'single' ? (
                <div>
                  <p className="text-gray-700 mb-2">确定要删除这项收藏吗？</p>
                  {deleteConfirm.item && (
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="font-medium text-gray-900">
                        {deleteConfirm.item.title || deleteConfirm.item.work_title}
                        {deleteConfirm.item.title && deleteConfirm.item.work_title && 
                          ` - ${deleteConfirm.item.title}`
                        }
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-gray-700 mb-2">确定要删除所选的收藏项目吗？</p>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">
                      共选择了 {totalSelectedItems} 项：
                      {selectedItems.works.length > 0 && ` ${selectedItems.works.length} 个作品`}
                      {selectedItems.episodes.length > 0 && ` ${selectedItems.episodes.length} 个集数`}
                      {selectedItems.images.length > 0 && ` ${selectedItems.images.length} 张图片`}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, type: 'single' })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                disabled={deleteMutation.isPending || batchDeleteMutation.isPending}
              >
                取消
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center"
                disabled={deleteMutation.isPending || batchDeleteMutation.isPending}
              >
                {(deleteMutation.isPending || batchDeleteMutation.isPending) ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    删除中...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    确认删除
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 标题和统计 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回首页
          </button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Heart className="w-6 h-6 mr-2 text-red-500" />
            我的收藏
          </h1>
        </div>
        
        {statsData?.data && (
          <div className="text-sm text-gray-600">
            共 {statsData.data.total_items} 项收藏
            （{statsData.data.total_works} 作品，{statsData.data.total_episodes} 集数，{statsData.data.total_images} 图片）
          </div>
        )}
      </div>
      
      {/* 操作栏 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 bg-white border border-gray-200 rounded-lg p-4">
        {/* 搜索框 */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="搜索收藏内容..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        {/* 操作按钮 */}
        <div className="flex items-center space-x-3">
          {totalSelectedItems > 0 && (
            <>
              <span className="text-sm text-gray-600">
                已选择 {totalSelectedItems} 项
              </span>
              
              {/* 删除按钮 */}
              <button
                onClick={() => setDeleteConfirm({ show: true, type: 'batch' })}
                className="flex items-center px-3 py-2 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                删除选中
              </button>
              
              {/* 下载按钮（仅在图片标签页显示） */}
              {activeTab === 'images' && selectedItems.images.length > 0 && (
                <>
                  <div className="flex items-center space-x-2">
                    <select
                      value={downloadImageSize}
                      onChange={(e) => setDownloadImageSize(e.target.value as 'small' | 'large')}
                      className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="small">小图</option>
                      <option value="large">大图</option>
                    </select>
                    
                    <button
                      onClick={handleDownloadFavoriteImages}
                      disabled={selectedItems.images.length === 0}
                      className="flex items-center px-3 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      下载选中 ({selectedItems.images.length})
                    </button>
                  </div>
                </>
              )}
            </>
          )}
          
          <button
            onClick={handleExport}
            className="flex items-center px-3 py-2 text-sm text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors"
          >
            <FileDown className="w-4 h-4 mr-1" />
            导出
          </button>
        </div>
      </div>
      
      {/* 标签页导航 */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('works')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'works'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Film className="w-4 h-4" />
              <span>作品收藏</span>
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {statsData?.data?.total_works || 0}
              </span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('episodes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'episodes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Play className="w-4 h-4" />
              <span>集数收藏</span>
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {statsData?.data?.total_episodes || 0}
              </span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('images')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'images'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <ImageIcon className="w-4 h-4" />
              <span>图片收藏</span>
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {statsData?.data?.total_images || 0}
              </span>
            </div>
          </button>
        </div>
      </div>
      
      {/* 标签页内容 */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">正在加载收藏数据...</p>
          </div>
        </div>
      ) : (
        <div>
          {activeTab === 'works' && renderWorksTab()}
          {activeTab === 'episodes' && renderEpisodesTab()}
          {activeTab === 'images' && renderImagesTab()}
        </div>
      )}
    </div>
  )
}

export default FavoritesPage